<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'
	import { ProductPlatforms } from '~/server/type'
	import { ProductCode } from '~/types'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | Phone Insurance | Gadget insurance for businesses',
		description: 'Gadget insurance cover that protects your business assets from damages, spills and theft.',
		tags: ['Gadget insurance'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/gadget`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const { navigateToProduct, viewMoreProducts } = useProductsStore()

	const benefitHighlights = [
		{
			title: 'OEM Parts Replacement',
			description: 'Ensure quality repairs with original equipment manufacturer parts.',
			image: '/v1727082973/orm_s2dcks.webp',
			open: true
		},
		{
			title: 'Double Claims Annually',
			description:
				'Enjoy the flexibility of up to two claims per year ensuring that your gadgets are protected all year round.',
			image: '/v1727082970/dlc_a6nlgl.webp'
		},
		{
			title: 'Seamless claims payout',
			description: 'Guaranteed swift and digital claims application and payout on damaged gadgets.',
			image: '/v1727082971/scp_jybcgw.webp'
		},
		{
			title: 'Batch Upload',
			description: 'Streamline the process by adding multiple gadgets at once, saving you time and effort.',
			image: '/v1727082979/upload-gadget_ynxzjs.webp'
		},
		{
			title: 'Easy Plans Management',
			description:
				"Add or remove staff gadgets with ease, ensuring your coverage stays up to date with your team's needs.",
			image: '/v1727082975/seemless_hrdkys.webp'
		},
		{
			title: 'Employee Mobile App',
			description:
				'Give your team confidence to work anywhere, their devices are covered, and help is always one tap away with the MyCover app.',
			image: '/v1761842471/mobile-comms_ggywlb.webp'
		}
	]

	const faqList = ref<FAQContext[]>([
		{
			question: 'What is covered under Gadget Cover?',
			answer: 'It covers accidental screen damage, liquid damage, and provides a full replacement if your device is damaged beyond repair. All repairs are done with OEM parts and come with a 90-day warranty.',
			open: true
		},
		{
			question: 'How many claims can I make under the Gadget Cover policy?',
			answer: 'You are allowed up to two claims per annum.',
			open: false
		},
		{
			question: 'Is there an excess deduction for claims?',
			answer: "No, there is no excess deduction, meaning you don't have to pay anything for a claim to be processed",
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'Gadget Cover',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082963/all-smiles_anb50e.webp',
						url: seo.url,
						price: '0'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'Gadget Cover', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})


	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'laptop-2',
				height: 80,
				width: 80
			},
			title: 'Submit Device Details',
			description:
				'Enter the required information about your device, including its make, model, and serial number manually or through our batch upload.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'payment-card',
				height: '100',
				width: '100'
			},
			title: 'Complete Payment',
			description:
				'Once your device details are entered, proceed to make the payment for insurance coverage and finalize the policy.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'gadget-umbrella',
				height: '80',
				width: '80'
			},
			title: 'Receive Confirmation',
			description:
				'After completing the payment process, instantly receive confirmation of your gadget insurance coverage via email'
		}
	])

	const btnClass: H1Config = {
		tag: { base: 'bg-main text-white' },
		wrapperClass: {
			overflow: '',
			maxWidth: '',
			base: 'pb-10'
		},
		paragraphClass: {
			maxWidth: 'max-w-[634px] md:leading-[32px] md:text-center md:mx-auto',
			text: 'text-base md:text-lg lg:text-xl md:text-center text-balance'
		},
		btn: {
			base: 'md:mt-14 md:flex justify-center items-center  md:text-center flex-col ',

			primary: {
				btn: {
					base: 'bg-white  text-base text-gadget-800 hover:text-white',
					onHoverPseudo: 'hover:before:bg-gadget-500 hover:bg-gadget-500'
				},
				text: {
					size: ''
				}
			},
			secondary: {
				base: 'default-btn  max-w-[405px] text-center justify-center w-full gap-x-3',
				text: 'text-sm md:text-base'
			}
		},
		hClass: {
			wrapper: {
				size: 'text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem]'
			}
		}
	}

	const guideList: GuideListContext = [
		{
			tab: 'claim_process',
			label: 'Simple claims process',
			description:
				'We get the urgency – Simply report a claim on your dashboard and we’ll get our technicians to fix things in no time at your preferred location.',
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'clipboard-text',
						height: 16
					},
					title: 'Report Claim',
					description:
						'Damaged screen or your device took a dip, no worries, report your claim via your dashboard.',
					imageSrc: '/v1727082833/gadget-claim-1_sbdcpj.gif',
					show: true
				},
				{
					title: 'Claims assessment',
					description: 'Your claim is quickly reviewed to ensure a smooth process.',
					iconTag: {
						dir: 'random',
						icon: 'clock'
					},
					imageSrc: '/v1727082838/gadget-claim-2_rfqxpt.gif'
				},
				{
					title: 'Instant payout',
					description:
						"Got the green light? Enjoy speedy payouts for approved claims – because getting back to work shouldn't be a wait.",
					iconTag: {
						dir: 'random',
						icon: 'receipt'
					},
					imageSrc: '/v1727082957/gadget-claim-3_rals23.webp'
				}
			]
		}
	]

	const teamBenefits: teamBenefitsContext = [
		{
			title: '90-day warranty repair',
			description:
				"This means that if anything unexpected arises within the 90-day timeframe related to the initial repair, it'll be fixed free of charge.",
			icon: {
				icon: 'badge',
				dir: 'random'
			}
		},
		{
			title: 'Screen damage',
			description:
				'Minimise downtime and safeguard your work by keeping your devices protected from accidental screen damage.',
			icon: {
				icon: 'laptop',
				dir: 'random'
			}
		},
		{
			title: 'Liquid damage',
			description:
				'Your work devices are covered from liquid damage, ensuring they receive expert repairs by skilled technicians.',
			icon: {
				icon: 'water-drops',
				dir: 'random'
			}
		},
		{
			title: 'Device theft',
			description:
				'Keep your business running smoothly even if work devices are stolen. Our insurance ensures quick replacement, minimising downtime.',
			icon: {
				icon: 'thief',
				dir: 'random'
			}
		},
		{
			title: 'Full value reimbursement',
			description:
				'If our skilled technicians determine that your device cannot be repaired, we’ll fully reimburse you for the device’s value.',
			icon: {
				icon: 'value',
				dir: 'random'
			}
		},
		{
			title: 'Enhanced Cover',
			description:
				'Laptops are crucial to your operations. Get more coverage on charging ports, chargers, & keyboards.',
			icon: {
				icon: 'charger',
				dir: 'random'
			}
		}
	]
	const carouselList = ['/v1727082963/all-smiles_anb50e.webp', '/v1727082987/tuff_gzbuiv.png']
</script>

<template>
	<div>
		<header class="mt-0 bg-gadget-800 md:px-0 sm:min-h-[770px] min-h-[700px] pt-24">
			<ui-pad :ui="{ paddingBlock: '' }" class="w-full">
				<ui-h1
					paragraph="Gadgets are the lifeline of your business operations, protect them from accidental damages, spills, and theft."
					tag="Gadget Cover"
					:ui="btnClass"
					primary-btn-text="Protect your gadgets in 5 minutes <span class='max-sm:hidden'>or less</span>"
					secondary-btn-text="Speak to us to get on board"
					:action="{ type: 'onboard', product: ProductCode.GADGET }"
				>
					<template #title="{ highlightColor }">
						<span class="text-pretty">
							The insurance that keeps your <br />
							<span :class="highlightColor">business connected</span>
						</span>
					</template>
				</ui-h1>
			</ui-pad>
		</header>

		<animated-gadget-frame
			:ui="{
				wrapper: {
					display: 'block',
					base: 'w-full justify-center max-w-[1020px] mx-auto relative -top-28 xs:-top-28 sm:-top-[180px]',
					height: '',
					padding: 'px-5 lg:px-0'
				},
				frame: {
					height: 'max-h-auto',
					rounded: 'rounded-lg'
				}
			}"
		>
			<template #image>
				<div class="relative top-[18px] sm:top-5 lg:top-[30px] -left-3 md:-left-2 lg:-left-[30px]">
					<tools-carousel
						:carousel-list="carouselList"
						:options="{
							perPage: 1,
							autoWidth: true,
							gap: 20,
							arrows: false,
							pagination: false,
							drag: true
						}"
						:indicator="{ class: 'bg-gadget-500' }"
					>
						<template #default="{ item, index }">
							<nuxt-img
								:src="item"
								:alt="`Gadget protection illustration - ${index === 0 ? 'Protected person' : 'Insured device'}`"
								loading="lazy"
								class="flex-none snap-start h-full max-h-[210px] md:max-h-[310px] max-md:max-w-[300px] object-cover rounded-lg"
							/>
						</template>
					</tools-carousel>
				</div>
			</template>
		</animated-gadget-frame>

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />
			<lazy-main-benefit-description
				:highlighted-benefits="benefitHighlights"
				title="End-to-end solutions for unmatched protection"
				class="space-y-5"
				:ui="{ li: { activeClass: 'border-gadget-500', activeText: 'text-gadget-500' } }"
			>
				<div class="max-w-[712px] w-full text-center mx-auto flexed flex-col">
					<p class="sm:text-xl text-base text-gray-600">
						We offer you tools & solutions to seamlessly manage your gadget insurance plans, keeping your
						gadgets shielded with the utmost care.
					</p>
				</div>
			</lazy-main-benefit-description>
			<ui-spacer />

			<lazy-main-productivity-benefits
				title="Stay connected"
				description="Let's get you onboarded with the complete protection you need for your tools."
				:team-benefits="teamBenefits"
				product-name="gadget cover"
				:ui="{
					container: {
						btn: {
							base: 'bg-gadget-500 min-h-[56px]',
							onHoverPseudo: 'hover:before:bg-gadget-800'
						}
					}
				}"
				@trigger="showBenefits = true"
			>
				<template #hover-icon>
					<tools-icon dir="random" icon="monitor-mobile" />
				</template>
			</lazy-main-productivity-benefits>
		</ui-pad>
		<ui-spacer />
		<lazy-client-only>
			<lazy-main-gadget-pricing />
		</lazy-client-only>
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-information-hint category="gadget" />
			<ui-spacer />
			<lazy-molecules-process-steps
				title="Convenient. Efficient. Fast."
				description="Secure your gadgets in 3 easy steps!"
				:processes="onboardingProcesses"
				:ui="{ strokeText: { strokeColor: '#E8B0E1', color: 'text-gadget-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-gadget-500 mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-gadget-800'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct(ProductCode.GADGET)"
					>
						<span>Get Covered Now</span>
						<template #icon>
							<tools-icon dir="random" icon="monitor-mobile" :height="15" />
						</template>
					</ui-btn>
				</template>
			</lazy-molecules-process-steps>
		</ui-pad>

		<ui-spacer />

		<lazy-molecules-important-guides :guide-list="guideList" />
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />

		<molecules-demo-card
			description=""
			title="Comprehensive coverage for your business."
			type="gadget"
			@secondary-click="viewMoreProducts('gadget')"
		/>

		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>

		<ui-spacer />

		<molecules-benefit-dialog
			v-model:show="showBenefits"
			:product-name="ProductCode.GADGET"
			product-category="gadget"
			:platform="ProductPlatforms.BUSINESS"
		/>
		<molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
